import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { deacService } from '../../../services/central-api/deac';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';
import { WindowInformation } from '../../../services/window-information';

interface ModalProps {
    selectedApplications: Presence[];
    setSelectedApplications: Dispatch<SetStateAction<Presence[]>>;
    currentPresence: Presence;
    isAssignmentModalVisible: boolean;
    setIsAssignmentModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedAssignment: number;
    setSelectedAssignment: Dispatch<SetStateAction<number>>;
}

export default function AssignmentModal({
    selectedApplications,
    setSelectedApplications,
    currentPresence,
    isAssignmentModalVisible,
    setIsAssignmentModalVisible,
    selectedAssignment,
    setSelectedAssignment
}: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectableAssignments, setSelectableAssignments] = useState<{ label: string; value: number; }[]>([]);
    const [isSuggestedAssignment, setIsSuggestedAssignment] = useState<boolean>(false);

    async function getAssignments(vacancyApplicationId: number) {
        try {
            setIsLoading(true);
            const assignments = await deacService.getAssignmentsByVacancyApplication(vacancyApplicationId);
            setSelectableAssignments([
                ...assignments.map((assignment) => {
                    return {
                        value: assignment.id,
                        label: assignment.name
                    };
                })
            ]);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isAssignmentModalVisible) {
            setIsSuggestedAssignment(currentPresence.present == null && currentPresence.assignmentId != undefined);
            getAssignments(currentPresence.id);
        }
    }, [currentPresence, isAssignmentModalVisible]);

    return (<Modal
        transparent={true}
        visible={isAssignmentModalVisible}
        animationType='fade'
        onRequestClose={() => setIsAssignmentModalVisible(false)}
    >
        <View style={styles.modalContainer}>
            <View style={styles.formContainer(windowInfo.isMobile)}>
                <View style={styles.modalHeader}>
                    <MyAppText style={styles.modalHeaderText}>{translate('assignments')}</MyAppText>
                    {isSuggestedAssignment &&
                        <MyAppText style={styles.modalHeaderInfo}>{translate('suggestedAssignmentByUnit')}</MyAppText>
                    }
                    <FilterDropdown
                        isLoading={isLoading}
                        items={selectableAssignments}
                        multiple={false}
                        setValue={setSelectedAssignment}
                        value={selectedAssignment}
                        disabled={false}
                        zIndex={2}
                    />
                </View>
                <FormActions
                    onSubmit={() => {
                        if (selectedAssignment) {
                            const assignment = selectableAssignments.find(a => a.value == selectedAssignment);
                            if (!assignment) {
                                return;
                            }
                            const updated = selectedApplications.map(sa => {
                                if (sa.id === currentPresence.id) {
                                    return {
                                        ...sa,
                                        assignmentId: selectedAssignment,
                                        assignment: { id: selectedAssignment, name: assignment.label },
                                    };
                                }
                                return sa;
                            });
                            setSelectedApplications(updated);
                        }
                        setIsAssignmentModalVisible(false);
                    }}
                    onClose={() => setIsAssignmentModalVisible(false)}
                    disabled={selectedAssignment == 0}
                    confirmText='select' />
            </View>
        </View>
    </Modal>);
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: (isMobile: boolean) => ({
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
        maxWidth: isMobile ? '95%' : '25%'
    }),
    modalContainer: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    modalHeader: {
        flexDirection: 'column',
        gap: 10,
    },
    modalHeaderText: {
        color: theme.colors.neutral['500'],
        fontSize: 16,
        fontWeight: '600'
    },
    modalHeaderInfo: {
        color: theme.colors.neutral['400'],
        fontSize: 14
    },
}));
